import { MicroAppEntry, INSTANCE as ApplicationManager } from '@core/application/ApplicationManager'
import { registerMicroApps } from 'qiankun'
import { INSTANCE as DeviceManager } from '@core/devices/DeviceManager'
import { INSTANCE as GuideManager } from '@core/GuideManager'
import { INSTANCE as EventManager } from '@core/EventManager'
import { MicroAppContainer, PATH_SEPARATOR } from '@core/Constants'
import type { AppInfo, MicroAppRegister } from '@core/application/ApplicationManager'
import {
  SystemAppId,
  FileManagerList,
  getMicroAppIdFromUrl,
  convertToMicroAppId,
  AppCategory,
} from '@core/application/ApplicationConstants'
import { INSTANCE as FileUploadManager } from '@/core/transmission/FileUploadManager'
import { INSTANCE as FileDownloadManager } from '@/core/transmission/FileDownloadManager'
import { INSTANCE as NotificationManager } from '@/core/Notification/NotificationManager'
import { INSTANCE as WindowUnloadManager } from '@/core/WindowUnloadManager'
import { Env } from '@core/EnvUtils'
import { buildAsyncOperationEventDataBus } from '@/core/databus/DataBusFactory'
import { getMicroAppBaseUrl, getMicroAppPreviewBaseUrl } from '@/core/application/AppUtils'
import { INSTANCE as previewManager } from '@/core/application/PreviewWindowManager'
import { INSTANCE as certificateManager, TransmissionType } from '@core/transmission/CertificateManager'
import { INSTANCE as WindowPopperManager } from '@/core/WindowPopperManager'
import { debounce, isFunction } from 'lodash'
import { URLBuilder } from '@/core/Utils'
import { InvalidAccountAuthorityException } from '@/core/Exceptions'
import { INSTANCE as DevicePullUpTaskManager } from '@/core/DevicePull/DevicePullUpTaskManager'
import { getApiBaseOrigin, getAlbBaseOrigin } from '@/BuildConfig'
import { isMappReloginDisabled } from '@/DebugConfig'

const REQUEST_RELOGIN_DEBOUNCING_IN_MS = 1000
const REGISTER_APPS_NAVIGATOR_SUFFIX_NAME = 'Nav'
const CURRENT_DATA_REGION = 'CN'

const requestRelogin = debounce(
  () => {
    if (isMappReloginDisabled()) return
    // 由于该方法调用环境可能存在于异步环境中，throw异常会被catch，所以这里的异常使用Promise.reject进行抛出
    Promise.reject(new InvalidAccountAuthorityException())
  },
  REQUEST_RELOGIN_DEBOUNCING_IN_MS,
  { leading: true, trailing: false }
)

const baseOrigin = {
  apiBaseOrigin: getApiBaseOrigin(),
  albBaseOrigin: getAlbBaseOrigin(),
  region: CURRENT_DATA_REGION,
}

const getBaseOrigin = () => baseOrigin

const appsRegister: MicroAppRegister = (apps: AppInfo[], base: string) => {
  const notificationManager = Env.isTest() || Env.isLocalDev() ? NotificationManager : undefined
  const guideManager = GuideManager.internalInstance
  const registerApps = []

  for (const app of apps.filter(
    ({ category, isCloudService, id }) =>
      !ApplicationManager.isPlaceHolderApplication(id) && category !== AppCategory.SYSTEM && !isCloudService
  )) {
    try {
      registerApps.push({
        name: app.id,
        entry: new URLBuilder(app.entry).appendPath(MicroAppEntry.INDEX).toString(),
        container: MicroAppContainer.MAIN_CONTENT,
        activeRule: [
          Array.of(getMicroAppBaseUrl(), app.id).join(PATH_SEPARATOR),
          Array.of(getMicroAppPreviewBaseUrl(), app.id).join(PATH_SEPARATOR),
        ],
        props: {
          deviceManager: DeviceManager.readonlyInstance,
          pageManager: ApplicationManager.getPageManager(app.id),
          //TODO: 后续移入popupWindowManager，删除guideManager
          /**
           * @deprecated
           */
          guideManager,
          eventManager: EventManager.getManager(app.id),
          fileUploader: FileUploadManager.getManager(app.id),
          fileDownloader: FileDownloadManager.getManager(app.id),
          previewManager,
          DevicePullUpTaskManager: DevicePullUpTaskManager.internalInstance,
          popupWindowManager: {
            showDeviceGuide: () => guideManager.showDeviceGuide(),
            /**
             * @deprecated
             */
            showConnectGuide: (deviceId: string) => guideManager.showConnectGuide(deviceId),
            showCertificateGuide: (neutralBtnCallback: () => void, deviceId?: string) => {
              if (deviceId) {
                certificateManager.transmissionType = TransmissionType.FILE_OPEN
                certificateManager.showLANAvailabilityNotice({ extraQuery: `deviceId=${deviceId}` })
              }
              if (isFunction(neutralBtnCallback)) neutralBtnCallback()
            },
          },
          notificationManager,
          ...(app.id === SystemAppId.NC_DEMO ? { asyncOperationEvent: buildAsyncOperationEventDataBus() } : {}),
          windowUnloadManager: WindowUnloadManager,
          requestRelogin,
          getBaseOrigin,
        },
      })

      if (app.hasSubNavigation) {
        registerApps.push({
          name: app.id + REGISTER_APPS_NAVIGATOR_SUFFIX_NAME,
          entry: new URLBuilder(app.entry).appendPath(MicroAppEntry.NAVIGATOR).toString(),
          container: MicroAppContainer.NAVIGATOR_MENU,
          activeRule: [
            Array.of(getMicroAppBaseUrl(), app.id).join(PATH_SEPARATOR),
            // 特殊处理file一级目录下在kdocs等appId下也需要激活
            (location: Location) => {
              const appId = convertToMicroAppId(app.id)
              const appIdByLocation = getMicroAppIdFromUrl(location.href)

              return (
                !!appIdByLocation &&
                FileManagerList.includes(appIdByLocation) &&
                !!appId &&
                FileManagerList.includes(appId)
              )
            },
          ],
          props: {
            pageManager: ApplicationManager.getPageManager(app.id),
            deviceManager: DeviceManager.readonlyInstance,
            guideManager: GuideManager.internalInstance,
            getBaseOrigin,
          },
        })
      }
    } catch (error) {
      console.error(error)
    }
  }

  registerMicroApps(registerApps, {
    beforeLoad: (app) => {
      console.log('MAIN_APP Lifecycle beforeLoad', app.name)

      return Promise.resolve()
    },

    afterMount: (app) => {
      console.log('MAIN_APP Lifecycle afterMount', app.name)

      return Promise.resolve()
    },
  })
}

ApplicationManager.registerMicroApps(appsRegister)
